import ClearIcon from '@mui/icons-material/Clear';
import Alert from '@mui/material/Alert';
import { AutocompleteRenderGetTagProps } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from '@mui/material/Button';
import Chip from "@mui/material/Chip";
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from "@mui/material/Typography";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import DeleteSVG from "../../../../../public/assets/icons/delete.svg";
import theme from "../../../../theme/Theme";
import { AudienceConfig, OptionEntry } from '../../../../types/cqa';
import { cqaDistributionMetadata } from '../../../../utils/api';
import {
    calculateRemainingVolume,
    calculateSpecificCountRemainingVolume,
    calculateSpecificPercentageRemainingVolume,
    redistributeEqualVolume,
    validAssignedVolume
} from '../../../../utils/cqa/assignmentUtil';
import MultiSelectAutocomplete from "../../../atoms/autocomplete/multiselectautocomplete";
import Dropdown from '../../../atoms/dropdown/dropdown';
import "../ruleCommonSection.css";
import { ASTERISK_ELEMENT, DROPDOWN_STYLE, SELECTION_COLOR } from '../ruleCommonStyleBlocks';
import "./Audience.css";
import {
    LABEL_EQUAL_PERCENTAGES,
    LABEL_GROUP, LABEL_INDIVIDUAL,
    LABEL_SPECIFIC_COUNT,
    LABEL_SPECIFIC_PERCENTAGE,
    LABEL_SPECIFIC_VOLUME,
    LABEL_TYPE_WORK_PROJECT,
    LABEL_TYPE_WORK_PROJECT_COMPARE
} from '../constants';
import {
    CSS_CHIP,
    LIST_ITEM_STYLE,
    MENU_LIST_STYLE,
    PAPER_PROPS_STYLE
} from './style-blocks';

interface Props {
    config?: AudienceConfig;
    codersOptions?: OptionEntry[];
    codersSelected?: OptionEntry[];
    groupOptions?: OptionEntry[];
    groupsSelected?: OptionEntry[];
    hideGroupSelection?: boolean;
    projectId: string;
    typeOfWork: string;
    setConfig?: Dispatch<SetStateAction<{ [key: string]: any }>>;
    setErrorMessages?: React.Dispatch<React.SetStateAction<any[]>>;
    setErrorOpen?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Audience = (props: Props): JSX.Element => {
    const [distributionMethod, setDistributionMethod] = useState("");
    const [distributionVolume, setDistributionVolume] = useState("");
    const [groups, setGroups] = useState<OptionEntry[]>([]);
    const [remainingVolume, setRemainingVolume] = useState(0);
    const [assignableCount, setAssignableCount] = useState(NaN);
    const [processingCount, setProcessingCount] = useState(0);
    const [usersSelected, setUsersSelected] = useState<OptionEntry[]>([]);
    const [volumeType, setVolumeType] = useState("");
    const disableDistribution = assignableCount === 0 || processingCount > 0;
    const projectCompareMode = LABEL_TYPE_WORK_PROJECT_COMPARE === props.typeOfWork;

    /*
        Helper/state functions
    */

    const clearUsersSelected = () => {
        updateConfigInviduals([]);
        setUsersSelected([]);
    };

    const updateConfigInviduals = (individuals: OptionEntry[]) => {
        props.setConfig(current => ({ ...current, individuals }));
    };

    const updateConfigDistributionVolume = (value: string) => {
        setDistributionVolume(value);
        props.setConfig(current => ({ ...current, distributionVolume: value }));
    };

    const onChangeDistribution = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        // When switching from individual to group
        if (distributionMethod === LABEL_INDIVIDUAL && value === LABEL_GROUP) {
            onChangeDistributionVolume(null, "");
            onChangeVolumeType("");
            clearUsersSelected();
        }

        setDistributionMethod(value);
        props.setConfig(current => ({ ...current, distributionMethod: value }));
    }

    const onChangeGroupSelect = (selectedValues: OptionEntry[]) => {
        setGroups(selectedValues);
        props.setConfig(current => ({ ...current, groupVisibility: selectedValues }));
    };

    const updateRemainingVolume = (updatedUsersSelected: OptionEntry[], newVolumeType: string, newRemainingValue: number) => {
        const result = calculateRemainingVolume(updatedUsersSelected, newVolumeType, newRemainingValue);
        setRemainingVolume(result);
    };

    const onUserSelect = (value: OptionEntry) => {
        if (!volumeType && distributionVolume === LABEL_SPECIFIC_VOLUME) {
            props.setErrorMessages(["Please select volume type"]);
            props.setErrorOpen(true);
            return;
        }

        const updatedUsers = [...usersSelected, value];

        if (distributionVolume === LABEL_EQUAL_PERCENTAGES) {
            if (props.config?.distributionVolume !== LABEL_EQUAL_PERCENTAGES) {
                // This if block fixes a bug that occurs when a user is setting up audience distribution
                // for the first time and uses equal percentage. The UI and react state defaults to equal
                // percentage but the project_configuration is never updated.
                updateConfigDistributionVolume(LABEL_EQUAL_PERCENTAGES);
            }
            redistributeEqualVolume(updatedUsers);
        }

        setUsersSelected(updatedUsers);
        updateConfigInviduals(updatedUsers);
    };

    const onUserRemove = (entry: OptionEntry) => {
        const updatedUsers = usersSelected.filter((us => us.value !== entry.value));

        if (distributionVolume === LABEL_EQUAL_PERCENTAGES) {
            redistributeEqualVolume(updatedUsers);
        }

        setUsersSelected(updatedUsers);
        updateConfigInviduals(updatedUsers);
        updateRemainingVolume(updatedUsers, volumeType, assignableCount);
    }

    const onChangeDistributionVolume = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        clearUsersSelected();
        updateConfigDistributionVolume(value);

        if (value === LABEL_EQUAL_PERCENTAGES) {
            onChangeVolumeType("");
        }
    };

    const onChangeVolumeType = (value: string | OptionEntry) => {
        if (typeof value === "string") {
            setVolumeType(value);
            props.setConfig(current => ({ ...current, volumeType: value }));
            clearUsersSelected();
            setRemainingVolume(value === LABEL_SPECIFIC_PERCENTAGE ? 100 : assignableCount);
        }
    }

    const copyUsersSelectedAndUpdateUserVolume = (user: OptionEntry, newVolume: number) => {
        return usersSelected.map((u) => {
            if (u.value === user.value) {
                return {
                    ...u,
                    volume: newVolume,
                } as OptionEntry;
            }

            return { ...u }
        });
    };

    const onChangeSelectedUserVolume = (user: OptionEntry, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(event.target.value);

        if (!value && value != 0) {
            return;
        }

        const updatedUsers = copyUsersSelectedAndUpdateUserVolume(user, value);

        if (validAssignedVolume(updatedUsers, volumeType, assignableCount)) {
            setUsersSelected(updatedUsers);
            updateConfigInviduals(updatedUsers);

            if (volumeType === LABEL_SPECIFIC_COUNT) {
                setRemainingVolume(
                    calculateSpecificCountRemainingVolume(updatedUsers, assignableCount)
                );
            }

            if (volumeType === LABEL_SPECIFIC_PERCENTAGE) {
                setRemainingVolume(
                    calculateSpecificPercentageRemainingVolume(updatedUsers)
                );
            }
        } else {
            setUsersSelected(copyUsersSelectedAndUpdateUserVolume(user, user.volume || 0));
            props.setErrorMessages(["Total volume has exceeded 100% assigment"]);
            props.setErrorOpen(true);
        }
    };

    /*
        State management
    */

    useEffect(() => {
        const audience = props.config;

        setDistributionMethod(props.projectId ? audience?.distributionMethod : null);
        setDistributionVolume(projectCompareMode ? null : audience?.distributionVolume || LABEL_EQUAL_PERCENTAGES);
        setGroups(audience?.groupVisibility || []);
        setVolumeType(audience?.volumeType);
        setUsersSelected(audience?.individuals || []);
    }, [props]);

    useEffect(() => {
        if (props.projectId) {
            cqaDistributionMetadata(props.projectId)
                .then((resp) => {
                    setAssignableCount(resp.assignable_count);
                    setRemainingVolume(resp.unassigned_count);
                    setProcessingCount(resp.processing_count || 0);
                })
                .catch((error) => {
                    console.error(error);
                    props.setErrorMessages(["Failed to fetch audience metadata"]);
                    props.setErrorOpen(true);
                })
        }
    }, [props.projectId]);

    return (
        <Box>
            {/* Container Title */}
            <Box className="setting_section_title_container">
                <Box className="setting_section_text_container" style={{ width: "74px" }}>
                    <span className="setting_section_text">
                        Audience
                    </span>
                </Box>
            </Box>

            <Box
                id="audience-config"
                className='setting_section_main_container'
                sx={{ border: `1px solid ${theme.palette.artisticStroke200.main}`, minHeight: "382px" }}
            >
                {/* Visibility Section */}
                <Box className="section_container" sx={{ height: "52px" }}>
                    <Typography className="setting_section_input_label">
                        Visibility {ASTERISK_ELEMENT}
                    </Typography>

                    <Box className="visibility_dropdown_container">
                        <MultiSelectAutocomplete
                            filterSelectedOptions={true}
                            onChange={onChangeGroupSelect}
                            options={props.groupOptions}
                            placeholder={(groups && groups.length > 0 || distributionMethod !== LABEL_GROUP) ? "" : "Select option"}
                            selectedValues={groups}
                            customDisplayChip={(index: number, label: string, getTagProps: AutocompleteRenderGetTagProps) => (
                                <Chip
                                    deleteIcon={<ClearIcon fontSize="small" style={{ width: 12, height: 12, color: "black" }} />}
                                    key={index}
                                    label={<span>{label}</span>}
                                    sx={{ ...CSS_CHIP, backgroundColor: `${theme.palette.artisticStructural300.main}`, }}
                                    className="multiselect_chip"
                                    {...getTagProps({ index })}
                                /> as JSX.Element
                            )}
                        />
                    </Box>
                </Box>
                {/* warn message */}
                {
                    disableDistribution && (
                        <Box>
                            {
                                assignableCount === 0 && (
                                    <Alert severity="warning">There are no assignable work items.</Alert>
                                )
                            }
                            {
                                processingCount > 0 && (
                                    <Alert severity="warning">Work item tasks are still processing - please check back shortly.</Alert>
                                )
                            }
                        </Box>
                    )
                }
                {/* Distribution Section */}
                {groups.length > 0 && (
                    <>
                        <Box>
                            <Box className="section_container">
                        <Typography className="setting_section_input_label">
                            Distribution method {ASTERISK_ELEMENT}
                        </Typography>
                    </Box>
                    <FormControl>
                        <RadioGroup
                            data-testid="distributionMethodRadioGroup"
                            onChange={onChangeDistribution}
                            name="distributionMethodRadioGroup"
                            row
                            sx={{ gap: "24px" }}
                            value={distributionMethod}
                        >
                            <FormControlLabel
                                data-testid="radioLabelGroup"
                                disabled={!props.projectId || projectCompareMode}
                                value={LABEL_GROUP}
                                control={<Radio sx={SELECTION_COLOR} />}
                                label="Group (s)"
                                hidden={props.hideGroupSelection}
                            />
                            <FormControlLabel
                                data-testid="radioLabelIndividual"
                                disabled={!props.projectId}
                                value={LABEL_INDIVIDUAL}
                                control={<Radio sx={SELECTION_COLOR} />}
                                label="Individual (s)"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                {
                    distributionMethod === LABEL_INDIVIDUAL && (
                        <Box className="distribution_volume_container">
                            {
                                [LABEL_TYPE_WORK_PROJECT].includes(props.typeOfWork) && (<Box className="distribution_volume_form_container">
                                    {/* Distribition Volume */}
                                    <Box className="distribution_volume_section">
                                        <Typography className="setting_section_input_label">
                                            Distribution volume {ASTERISK_ELEMENT}
                                        </Typography>

                                        <FormControl>
                                            <RadioGroup
                                                onChange={onChangeDistributionVolume}
                                                name="distributionVolumeRadioGroup"
                                                row
                                                sx={{ gap: "24px" }}
                                                value={distributionVolume}
                                            >
                                                <FormControlLabel
                                                    data-testid="radioLabelEqualPercentages"
                                                    value={LABEL_EQUAL_PERCENTAGES}
                                                    control={<Radio sx={SELECTION_COLOR} />}
                                                    label="Equal Percentages"
                                                    disabled={disableDistribution}
                                                />
                                                <FormControlLabel
                                                    data-testid="radioLabelSpecificVolume"
                                                    value={LABEL_SPECIFIC_VOLUME}
                                                    control={<Radio sx={SELECTION_COLOR} />}
                                                    label="Specific Volume"
                                                    disabled={disableDistribution}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>

                                    {/* Volume Type */}
                                    {
                                        distributionVolume === LABEL_SPECIFIC_VOLUME && (
                                            <Box className="volume_type_container">
                                                <Typography className="setting_section_input_label">
                                                    Volume Type {ASTERISK_ELEMENT}
                                                </Typography>
                                                <Dropdown
                                                    loading={false}
                                                    onChange={onChangeVolumeType}
                                                    options={[LABEL_SPECIFIC_PERCENTAGE, LABEL_SPECIFIC_COUNT]}
                                                    placeholder="Please select"
                                                    selectedValue={volumeType}
                                                    menuListStyle={{ minWidth: "160px" }}
                                                    paperPropsStyle={{}}
                                                    sx={{ ...DROPDOWN_STYLE }}
                                                />
                                            </Box>
                                        )
                                    }
                                </Box>)
                            }

                            <Box
                                className="users_selected_container"
                            >
                                {
                                    usersSelected.map((entry) => (
                                        <Box sx={{ display: "flex", justifyContent: "space-between" }} key={`${entry.label}_${entry.value}`}>
                                            <Box className="users_selected_name" sx={{ width: projectCompareMode ? "542px" : "265px" }}>
                                                <Typography className="users_selected_typography" data-testid={`${entry.label}_${entry.value}`}>
                                                    {entry.label}
                                                </Typography>
                                            </Box>

                                            {
                                                [LABEL_TYPE_WORK_PROJECT].includes(props.typeOfWork) && (<input
                                                    autoFocus={false}
                                                    data-testid={`${entry.label}_${entry.volume}`}
                                                    onChange={(event) => onChangeSelectedUserVolume(entry, event)}
                                                    style={{
                                                        fontFamily: "Roboto",
                                                        fontWeight: "400px",
                                                        fontStyle: "Regular",
                                                        fontSize: "14px",
                                                        lineHeight: "16px",
                                                        width: "265px",
                                                        height: "32px",
                                                        gap: "8px",
                                                        borderRadius: "3px",
                                                        borderWidth: "1px",
                                                        padding: "8px",
                                                        backgroundColor: "#FFFFFF",
                                                        border: "1px solid #A9D5E5"
                                                    }}
                                                    value={entry.volume}
                                                    disabled={LABEL_EQUAL_PERCENTAGES === distributionVolume}
                                                />)
                                            }

                                            <Button
                                                data-testid={`deleteButton_${entry.value}`}
                                                onClick={() => onUserRemove(entry)}
                                                style={{
                                                    width: "32px",
                                                    minWidth: "32px",
                                                    height: "32px",
                                                    gap: "4px",
                                                    opacity: 1,
                                                    borderRadius: "3px",
                                                    borderWidth: "1px",
                                                    padding: "8px",
                                                    border: "1px solid #8CB4C2",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Box sx={{
                                                    width: "16px",
                                                    height: "16px",
                                                }}>
                                                    <img src={DeleteSVG} />
                                                </Box>
                                            </Button>
                                        </Box>
                                    ))
                                }
                                {
                                    (distributionVolume === LABEL_SPECIFIC_VOLUME && usersSelected.length > 0)
                                    && [LABEL_TYPE_WORK_PROJECT].includes(props.typeOfWork)
                                    && (
                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "right",
                                        }}>
                                            <span style={{
                                                height: "10px",
                                                color: "black",
                                                minWidth: "300px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "right",
                                                borderRadius: "5px",
                                            }}>
                                                Remaining amount to distribute: {remainingVolume}{volumeType === LABEL_SPECIFIC_PERCENTAGE ? "%" : " Cases"}
                                            </span>
                                        </Box>
                                    )
                                }
                                <Dropdown
                                    placeholder="Search users"
                                    loading={false}
                                    options={
                                        props.codersOptions?.filter(opt =>
                                            !Array.from(usersSelected.map((us) => us.value)).includes(opt.value)
                                        )
                                    }
                                    selectedValue={null}
                                    onChange={onUserSelect}
                                    dropdownStyle={{}}
                                    listItemStyle={{ ...LIST_ITEM_STYLE, color: `${theme.palette.artisticTextHigh.main}` }}
                                    paperPropsStyle={PAPER_PROPS_STYLE}
                                    menuListStyle={MENU_LIST_STYLE}
                                    disabled={disableDistribution}
                                />
                            </Box>
                        </Box>
                    )
                }
                    </>
                )}

            </Box >
        </Box>
    );
}

Audience.defaultProps = {
    codersOptions: [],
    hideGroupSelection: true,
    setConfig: () => { },
    setErrorMessages: () => { },
    setErrorOpen: () => { },
};